import React from "react";
import Card from "components/card";
import Statistics from "../../overview/widgets/NursingStatistics";
import DollarIcon from "components/icons/WidgetIcon/DollarIcon";
import { usePortfolioData } from "contexts/PortfolioContext";
import { formatCurrency } from "utils/formatUtils";
import { TbTrendingUp, TbZoomMoney, TbRefresh } from "react-icons/tb";
import { useTabs } from "contexts/TabContext";

const GraphQLFinancialWidget = () => {
    const { addTab } = useTabs();

    // Portfolio data
    const {
        currentPortfolioFacility,
        selectedPeriod,
        isPortfolioLoading,
        currentPortfolioName,
        refreshFinancialData
    } = usePortfolioData();

    // Get financial metrics from periods data structure
    const periods = currentPortfolioFacility?.periods;

    // Helper function to extract financial data from periods structure
    const extractFinancialData = (periods, selectedPeriod) => {
        if (!periods || !selectedPeriod) return null;

        // Get data from the selected period
        const periodData = periods[selectedPeriod];
        if (!periodData || !periodData.Categories) return null;

        const expenses = periodData.Categories.Expenses || [];
        const revenue = periodData.Categories.Revenue || [];
        const funding = periodData.Categories.Funding || [];

        // Extract Adjusted Revenue and Adjusted Expenses.
        // The new portfolio shape doesn't expose "Adjusted Expenses" — fall back to the Total expense row.
        const adjustedRevenueData = revenue.find(item => item.Type === "Adjusted Revenue");
        const adjustedExpensesData =
            expenses.find(item => item.Type === "Adjusted Expenses") ||
            expenses.find(item => item.Type === "Total");

        // Extract EBITDAR data
        const adjustedEbitdarData = expenses.find(item => item.Type === "Adjusted EBITDAR");

        // Medicaid Rate: the S3 shape no longer emits a dedicated "Medicaid Rate"
        // row. The Medicaid revenue row's PPD is now per-Medicaid-day (= the rate),
        // so pull it from there.
        const medicaidRevenueData = revenue.find(item => item.Type === "Medicaid");

        // Extract Grant and UPL data
        const grantData = funding.find(item => item.Type === "Grant Funding");
        const uplData = funding.find(item => item.Type === "UPL Funding");

        return {
            adjustedRevenue: adjustedRevenueData || {},
            adjustedExpenses: adjustedExpensesData || {},
            adjustedEbitdar: adjustedEbitdarData?.Annual || 0,
            // New shape exposes the margin in `Percentages` ("23.66%"); old shape used numeric PPD.
            adjustedEbitdarMargin:
                adjustedEbitdarData?.Percentages ||
                adjustedEbitdarData?.PPD ||
                0,
            medicaidRate: medicaidRevenueData?.PPD || 0,
            grant: grantData?.Annual || 0,
            upl: uplData?.Annual || 0,
            // Add metadata for display
            date: periodData.operational?.["End Date"] || '',
            lastUpdated: new Date().toISOString(),
            periodInfo: periodData.operational || {},
            selectedPeriodName: selectedPeriod
        };
    };

    const financialMetrics = extractFinancialData(periods, selectedPeriod);


    const IconButton = ({ icon: Icon, onClick, className, disabled = false }) => (
        <button
            onClick={onClick}
            disabled={disabled}
            className={`p-1 hover:bg-gray-100 rounded-full transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
        >
            <Icon className="w-5 h-5 text-gray-600" />
        </button>
    );

    // Calculate financial metrics from periods data
    const calculateFinancialMetrics = () => {
        if (!financialMetrics) return {};

        return {
            adjustedRevenue: financialMetrics.adjustedRevenue || {},
            adjustedExpenses: financialMetrics.adjustedExpenses || {},
            adjustedEbitdar: {
                annual: financialMetrics.adjustedEbitdar || 0,
                margin: financialMetrics.adjustedEbitdarMargin || 0,
            },
            medicaidRate: financialMetrics.medicaidRate || 0,
            grant: {
                annual: financialMetrics.grant || 0,
            },
            upl: {
                annual: financialMetrics.upl || 0,
            },
        };
    };

    const metrics = calculateFinancialMetrics();

    // Open a finance-chart drill-down as a new tab. Matches the deal-side wiring
    // in `pages/overview/widgets/GeneralFinance.jsx`: build the finance-charts
    // URL for the current portfolio and hand it to addTab so the router picks up
    // the correct FinanceChartPage.
    const handleIconClick = (chartType, chartTitle) => {
        const path = currentPortfolioName
            ? `/portfolio/${encodeURIComponent(currentPortfolioName)}/finance-charts/${chartType}`
            : `/portfolio/finance-charts/${chartType}`;
        addTab(chartType, chartTitle, path);
    };

    const handleRefreshFinancialData = async () => {
        if (currentPortfolioName) {
            await refreshFinancialData(currentPortfolioName);
        }
    };

    // Check if current facility has Grant funding data
    const shouldShowGrant = () => {
        return financialMetrics && financialMetrics.grant > 0;
    };

    // Check if current facility has UPL funding data
    const shouldShowUpl = () => {
        return financialMetrics && financialMetrics.upl > 0;
    };

    if (!financialMetrics) {
        return (
            <Card extra={"items-center justify-around w-full p-[16px] bg-cover gap-2"}>
                <div className="flex items-center justify-between w-full mb-2">
                    <h3 className="text-xl font-bold text-gray-700 dark:text-white">
                        General Finances
                    </h3>
                    <div className="flex gap-2">
                        <IconButton
                            icon={TbRefresh}
                            onClick={handleRefreshFinancialData}
                            disabled={isPortfolioLoading}
                        />
                    </div>
                </div>
                <div className="w-full flex justify-center items-center p-8">
                    <div className="text-gray-500 text-center">
                        <p>No financial data available</p>
                        <p className="text-sm mt-2">
                            Financial data will be automatically loaded when available.
                        </p>
                    </div>
                </div>
            </Card>
        );
    }

    return (
        <Card extra={"items-center justify-around w-full p-[16px] bg-cover gap-2"}>
            <div className="flex items-center justify-between w-full mb-2">
                <h3 className="text-xl font-bold text-gray-700 dark:text-white">
                    General Finances
                </h3>
                <div className="flex gap-2">
                    <IconButton
                        icon={TbRefresh}
                        onClick={handleRefreshFinancialData}
                        disabled={isPortfolioLoading}
                    />
                </div>
            </div>

            <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="relative">
                    <div className="absolute top-2 right-2 flex gap-1">
                        <IconButton
                            icon={TbZoomMoney}
                            onClick={() => handleIconClick('revenue-details', 'Revenue Details')}
                        />
                        <IconButton
                            icon={TbTrendingUp}
                            onClick={() => handleIconClick('revenue-trends', 'Revenue Trends')}
                        />
                    </div>
                    <Statistics
                        title="Adjusted Revenue"
                        subtitle1="Annual"
                        value1={formatCurrency(metrics.adjustedRevenue?.Annual || "")}
                        subtitle2="PPD"
                        value2={formatCurrency(metrics.adjustedRevenue?.PPD || "")}
                        icon={
                            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-lightPrimary text-4xl text-brand-500 dark:!bg-navy-700 dark:text-white">
                                <DollarIcon />
                            </div>
                        }
                    />
                </div>

                <div className="relative">
                    <div className="absolute top-2 right-2 flex gap-1">
                        <IconButton
                            icon={TbZoomMoney}
                            onClick={() => handleIconClick('expense-details', 'Expense Details')}
                        />
                        <IconButton
                            icon={TbTrendingUp}
                            onClick={() => handleIconClick('expense-trends', 'Expense Trends')}
                        />
                    </div>
                    <Statistics
                        title="Adjusted Expenses"
                        subtitle1="Annual"
                        value1={formatCurrency(metrics.adjustedExpenses?.Annual || "")}
                        subtitle2="PPD"
                        value2={formatCurrency(metrics.adjustedExpenses?.PPD || "")}
                        icon={
                            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-lightPrimary text-xl text-brand-500 dark:!bg-navy-700 dark:text-white">
                                <DollarIcon />
                            </div>
                        }
                    />
                </div>

                <div className="relative">
                    <div className="absolute top-2 right-2 flex gap-1">
                        <IconButton
                            icon={TbZoomMoney}
                            onClick={() => handleIconClick('ebitdar-details', 'EBITDAR Details')}
                        />
                        <IconButton
                            icon={TbTrendingUp}
                            onClick={() => handleIconClick('ebitdar-trends', 'EBITDAR Trends')}
                        />
                    </div>
                    <Statistics
                        title="Adjusted EBITDAR"
                        subtitle1="Annual"
                        value1={formatCurrency(metrics.adjustedEbitdar?.annual || "")}
                        subtitle2="Margin"
                        value2={(() => {
                            const margin = metrics.adjustedEbitdar?.margin;
                            if (margin === null || margin === undefined || margin === "" || margin === 0) return "-";
                            // If the value is already a pre-formatted string like "23.66%", show it directly.
                            if (typeof margin === "string" && margin.trim().endsWith("%")) {
                                return margin.trim();
                            }
                            const numValue = typeof margin === "number" ? margin : parseFloat(margin);
                            if (isNaN(numValue)) return "-";
                            return `${(numValue * 100).toFixed(0)}%`;
                        })()}
                        icon={
                            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-lightPrimary text-4xl text-brand-500 dark:!bg-navy-700 dark:text-white">
                                <DollarIcon />
                            </div>
                        }
                    />
                </div>

                <Statistics
                    title="Medicaid Rate"
                    value1={formatCurrency(metrics.medicaidRate || "")}
                    icon={
                        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-lightPrimary text-4xl text-brand-500 dark:!bg-navy-700 dark:text-white">
                            <DollarIcon />
                        </div>
                    }
                />

                {/* Only show Grant data for Highlands facility in Century portfolio */}
                {shouldShowGrant() && (
                    <div className="relative">
                        <div className="absolute top-2 right-2 flex gap-1">
                            <IconButton
                                icon={TbZoomMoney}
                                onClick={() => handleIconClick('grant-details', 'Grant Details')}
                            />
                            <IconButton
                                icon={TbTrendingUp}
                                onClick={() => handleIconClick('grant-trends', 'Grant Trends')}
                            />
                        </div>
                        <Statistics
                            title="Grant Revenue"
                            subtitle1="Annual"
                            value1={formatCurrency(metrics.grant?.annual || 0)}
                            icon={
                                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-lightPrimary text-4xl text-brand-500 dark:!bg-navy-700 dark:text-white">
                                    <DollarIcon />
                                </div>
                            }
                        />
                    </div>
                )}

                {/* Only show UPL data for Castle facilities */}
                {shouldShowUpl() && (
                    <div className="relative">
                        <div className="absolute top-2 right-2 flex gap-1">
                            <IconButton
                                icon={TbZoomMoney}
                                onClick={() => handleIconClick('upl-details', 'UPL Details')}
                            />
                            <IconButton
                                icon={TbTrendingUp}
                                onClick={() => handleIconClick('upl-trends', 'UPL Trends')}
                            />
                        </div>
                        <Statistics
                            title="UPL Funding"
                            subtitle1="Annual"
                            value1={formatCurrency(metrics.upl?.annual || 0)}
                            icon={
                                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-lightPrimary text-4xl text-brand-500 dark:!bg-navy-700 dark:text-white">
                                    <DollarIcon />
                                </div>
                            }
                        />
                    </div>
                )}
            </div>

            {/* Data Information */}
            {financialMetrics && financialMetrics.periodInfo && (
                <div className="w-full mt-4 p-3 bg-gray-50 rounded-lg">
                    <div className="text-sm text-gray-600">
                        <div className="flex justify-between items-start">
                            <div>
                                <div>
                                    <span className="font-medium">Selected Period:</span> {financialMetrics.selectedPeriodName}
                                </div>
                                <div className="mt-1">
                                    <span className="font-medium">Date Range:</span> {financialMetrics.periodInfo["Start Date"]?.slice(0, 4)}-{financialMetrics.periodInfo["Start Date"]?.slice(4, 6)}-{financialMetrics.periodInfo["Start Date"]?.slice(6, 8)} to {financialMetrics.periodInfo["End Date"]?.slice(0, 4)}-{financialMetrics.periodInfo["End Date"]?.slice(4, 6)}-{financialMetrics.periodInfo["End Date"]?.slice(6, 8)}
                                </div>
                            </div>
                            <div className="text-right">
                                <div>
                                    <span className="font-medium">Duration:</span> {financialMetrics.periodInfo["Period Length (Months)"]} months ({financialMetrics.periodInfo["Period Length (Days)"]} days)
                                </div>
                                <div className="mt-1">
                                    <span className="font-medium">Data Points:</span> {financialMetrics.periodInfo["Data Points"]}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Card>
    );
};

export default GraphQLFinancialWidget; 